import React, { useMemo, useRef, useState } from 'react'
import { SERIES, SERIES_SOFT, CONTEXT, CHROME, niceMax, ticks, linePath, areaPath } from '../../lib/viz'
import { money, moneyShort, formatDateShort } from '../../lib/format'

/**
 * Revenue over time, with an optional previous-period comparison.
 *
 * Hand-rolled SVG rather than a charting library. At this size a library buys
 * nothing and costs control: the mark specs below (2px line, 8px markers, a
 * crosshair that snaps to the nearest day, a baseline that is visually distinct
 * from the gridlines) are all things a library's defaults get subtly wrong and
 * that take longer to override than to write.
 *
 * The comparison series uses the **emphasis** form — current period in the
 * series hue, previous in a de-emphasis grey — rather than two categorical
 * colours. The subject is this period; last period is context. Two equal-weight
 * hues would make the reader work out which one they are meant to be looking at.
 *
 * Single y-axis only. Orders and revenue are never drawn on one pair of axes: a
 * second scale lets the author slide one series up or down against the other
 * until it tells whatever story they wanted, which is why it is the most
 * misleading thing a chart can do.
 */

const PAD = { top: 16, right: 16, bottom: 28, left: 52 }

const RevenueChart = ({
  data,
  comparison = null,
  height = 260,
  valueKey = 'revenue',
  formatValue = money,
  formatTick = moneyShort,
  seriesLabel = 'This period',
  comparisonLabel = 'Previous period',
}) => {
  const wrapRef = useRef(null)
  const [width, setWidth] = useState(720)
  const [hoverIndex, setHoverIndex] = useState(null)

  // ResizeObserver rather than a window listener: the chart also needs to react
  // to the sidebar collapsing, which never fires a window resize.
  React.useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(320, entry.contentRect.width))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const geometry = useMemo(() => {
    const rows = data ?? []
    const plotWidth = Math.max(width - PAD.left - PAD.right, 10)
    const plotHeight = Math.max(height - PAD.top - PAD.bottom, 10)

    const values = rows.map((r) => Number(r[valueKey]) || 0)
    const comparisonValues = (comparison ?? []).map((r) => Number(r[valueKey]) || 0)
    const max = niceMax(Math.max(1, ...values, ...comparisonValues))

    // Guard the single-point case: dividing by (n - 1) would be a divide by zero
    // and put the only marker at NaN.
    const stepX = rows.length > 1 ? plotWidth / (rows.length - 1) : 0
    const toPoint = (value, i) => ({
      x: PAD.left + (rows.length > 1 ? i * stepX : plotWidth / 2),
      y: PAD.top + plotHeight - (Math.max(0, value) / max) * plotHeight,
    })

    return {
      rows,
      max,
      plotWidth,
      plotHeight,
      baselineY: PAD.top + plotHeight,
      points: values.map(toPoint),
      comparisonPoints: comparisonValues.map(toPoint),
      stepX,
    }
  }, [data, comparison, width, height, valueKey])

  const { rows, max, baselineY, points, comparisonPoints } = geometry

  const handleMove = (event) => {
    if (rows.length === 0) return
    const box = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - box.left
    // Snap to the nearest day. A tooltip that only appears directly over a 2px
    // line is unusable on a trackpad.
    const ratio = (x - PAD.left) / Math.max(geometry.plotWidth, 1)
    const index = Math.round(ratio * (rows.length - 1))
    setHoverIndex(Math.min(rows.length - 1, Math.max(0, index)))
  }

  const axisTicks = ticks(max, 4)
  // Roughly one label per 90px, so they never collide at narrow widths.
  const labelEvery = Math.max(1, Math.ceil(rows.length / Math.max(2, Math.floor(width / 90))))

  const hovered = hoverIndex === null ? null : rows[hoverIndex]
  const hoveredPoint = hoverIndex === null ? null : points[hoverIndex]

  if (rows.length === 0) {
    return (
      <div className="flex h-[260px] items-center justify-center text-sm text-ink-muted">
        No sales in this period yet.
      </div>
    )
  }

  return (
    <div ref={wrapRef} className="relative w-full">
      {/* Legend. Always present for two series, so identity is never carried by
          colour alone. A single series needs none — the panel title names it. */}
      {comparison && (
        <div className="mb-2 flex items-center gap-4 text-2xs text-ink-secondary">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 rounded-full" style={{ background: SERIES }} />
            {seriesLabel}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 rounded-full" style={{ background: CONTEXT }} />
            {comparisonLabel}
          </span>
        </div>
      )}

      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${seriesLabel} ${valueKey} over ${rows.length} days`}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIndex(null)}
        className="overflow-visible"
      >
        {/* Gridlines, recessive: they orient the eye and must not compete with
            the data. */}
        {axisTicks.map((tick) => {
          const y = baselineY - (tick / max) * geometry.plotHeight
          return (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y}
                y2={y}
                stroke={tick === 0 ? CHROME.baseline : CHROME.grid}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y + 3.5}
                textAnchor="end"
                fontSize={10}
                fill={CHROME.inkMuted}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatTick(tick)}
              </text>
            </g>
          )
        })}

        {/* Previous period sits underneath, dashed, so it reads as reference. */}
        {comparisonPoints.length > 0 && (
          <path
            d={linePath(comparisonPoints)}
            fill="none"
            stroke={CONTEXT}
            strokeWidth={1.5}
            strokeDasharray="4 3"
            strokeLinejoin="round"
          />
        )}

        <path d={areaPath(points, baselineY)} fill={SERIES_SOFT} opacity={0.55} />
        <path
          d={linePath(points)}
          fill="none"
          stroke={SERIES}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Crosshair + marker for the hovered day. */}
        {hoveredPoint && (
          <g pointerEvents="none">
            <line
              x1={hoveredPoint.x}
              x2={hoveredPoint.x}
              y1={PAD.top}
              y2={baselineY}
              stroke={CHROME.baseline}
              strokeWidth={1}
            />
            {/* A surface-coloured ring keeps the marker legible wherever it
                lands — over the area fill, a gridline, or the other series. */}
            <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={5} fill={SERIES} stroke={CHROME.surface} strokeWidth={2} />
          </g>
        )}

        {/* X labels, thinned to whatever fits. */}
        {rows.map((row, i) =>
          i % labelEvery === 0 || i === rows.length - 1 ? (
            <text
              key={row.day ?? i}
              x={points[i].x}
              y={height - 8}
              textAnchor={i === 0 ? 'start' : i === rows.length - 1 ? 'end' : 'middle'}
              fontSize={10}
              fill={CHROME.inkMuted}
            >
              {formatDateShort(row.day)}
            </text>
          ) : null,
        )}
      </svg>

      {/* Tooltip in HTML, not SVG — real text wrapping and no font scaling. */}
      {hovered && hoveredPoint && (
        <div
          className="pointer-events-none absolute z-10 min-w-[150px] rounded-lg border border-hairline bg-white/95 px-3 py-2 shadow-raised backdrop-blur-sm"
          style={{
            left: Math.min(Math.max(hoveredPoint.x - 75, 0), width - 160),
            top: Math.max(hoveredPoint.y - 78, 0),
          }}
        >
          <div className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
            {formatDateShort(hovered.day)}
          </div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-ink">
            {formatValue(hovered[valueKey])}
          </div>
          <div className="text-2xs text-ink-secondary">
            {hovered.order_count ?? 0} order{(hovered.order_count ?? 0) === 1 ? '' : 's'}
            {hovered.units ? ` · ${hovered.units} items` : ''}
          </div>
        </div>
      )}
    </div>
  )
}

export default RevenueChart
